import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { firestore } from 'src/config/firebaseClient';
import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { CurrencyState } from '../../contexts/CurrencyContext';
import { Filter } from './ItemCatalogue';
import { Item } from 'functions/src/types/firestore/Game/Collection/Item';
import { Game } from 'functions/src/types/firestore/Game';
import { CryptoChain } from '../../util/CryptoChain';
import { UserProfile } from 'functions/src/types/firestore/User/UserProfile';
import { Droppable } from 'react-beautiful-dnd';
import {
  DraggableCatalogueGridItem,
  CatalogueGridItem,
  ExtendedItemCardPriced,
} from './CatalogueGridItems';
import { getFiltersMap } from '../../util/catalogue-grid';
import { Link } from '../Link';
import { toKebabCase } from '../../util/string';
import { getDocsInAll } from 'src/util/getDocsInAll';
import { useMobile } from 'src/hooks/useMobile';
import { getItemKey } from 'functions/src/util/items/getItemKey';
import { useAuth } from 'src/contexts/AuthContext';
import { NoResults } from './NoResults';
import { ConverterFactory } from 'functions/src/util/firestore/ConverterFactory';
import { LottieLoader } from '../LottieLoader';

export type CatalogueGridProps = {
  parent: 'liked' | 'inventory' | 'game';
  filters: { [name: string]: Filter };
  sortFunction: (
    a: ExtendedItemCardPriced,
    b: ExtendedItemCardPriced,
  ) => number;
  searchFunction: (input: ExtendedItemCardPriced) => boolean;
  currency: CurrencyState;
  supportedChains: CryptoChain[];
  isDragEnabled: boolean;
  handleItemClick?: (item: ExtendedItemCardPriced) => void;
  expandedItem?: ExtendedItemCardPriced;
  selectedSaleType: 'buy' | 'sell' | 'rent' | null;
} & Partial<Pick<Game<Date>, 'title' | 'id'>> &
  Partial<Pick<UserProfile<Date>, 'addresses' | 'likedPaths'>>;

export function CatalogueGrid({
  parent,
  filters,
  sortFunction,
  searchFunction,
  currency,
  addresses,
  id,
  title,
  isDragEnabled = false,
  handleItemClick,
  expandedItem,
  likedPaths,
  selectedSaleType,
}: CatalogueGridProps) {
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [filtersEnabled, setFiltersEnabled] = useState(false);
  const [items, setItems] = useState<ExtendedItemCardPriced[]>([]);
  const [filteredItems, setFilteredItems] = useState<ExtendedItemCardPriced[]>(
    [],
  );
  const observer = useRef<IntersectionObserver | null>(null);

  const filtersMap = getFiltersMap(filters, currency);
  const handleSelectItem = (item: ExtendedItemCardPriced) => {
    if (handleItemClick) {
      handleItemClick(item);
    }
  };
  const isMobile = useMobile();
  const { user } = useAuth();
  useEffect(() => {
    const handler = async () => {
      if (parent !== 'liked' || !likedPaths) {
        return;
      }
      const itemPaths = likedPaths?.filter((path) => {
        return (
          path.split('/').length === 6 &&
          path.startsWith('Game/') &&
          path.includes('Item/')
        );
      });
      const itemCards: ExtendedItemCardPriced[] = await Promise.all(
        itemPaths.map(async (path) => {
          const itemsRef = doc(firestore, path).withConverter(
            ConverterFactory.buildDateConverter<Item<Date>>(),
          );
          const itemDoc = await getDoc(itemsRef);
          return itemDoc.data() as ExtendedItemCardPriced;
        }),
      );
      setItems(itemCards);
    };
    handler();
  }, [likedPaths, parent]);
  const itemsQuery = useCallback(async () => {
    setLoading(true);
    switch (parent) {
      case 'inventory':
        const inventoryDocs = addresses
          ? await getDocsInAll({
              query: collectionGroup(firestore, 'Item').withConverter(
                ConverterFactory.buildDateConverter<Item<Date>>(),
              ),
              queryConstraints: [where('gameId', '==', id)],
              where: ['contract.ownerAddress', 'in', addresses],
            })
          : [];
        setLoading(false);
        return inventoryDocs.map((doc) => {
          return doc.data();
        });
      case 'game':
        const firestoreQuery = query(
          collectionGroup(firestore, 'Item').withConverter(
            ConverterFactory.buildDateConverter<Item<Date>>(),
          ),
          where('gameId', '==', id),
        );
        const result = await getDocs(firestoreQuery);
        setLoading(false);
        return result?.docs?.map((doc) => {
          return doc.data();
        });
      default:
        setLoading(false);
        return [];
    }
  }, [parent, id, addresses]);

  useEffect(() => {
    const handler = async () => {
      const itemsFromQuery = await itemsQuery();
      setItems(itemsFromQuery as ExtendedItemCardPriced[]);
    };
    if (parent !== 'liked') {
      handler();
    }
  }, [parent, itemsQuery]);

  useEffect(() => {
    const filtersArr: ((item: Item, userId: string) => boolean)[] = [];
    for (const [key, value] of Object.entries(filtersMap)) {
      if (filters[`${key}`] && filters[`${key}`].enabled) {
        filtersArr.push(value);
      }
    }
    if (filtersArr.length > 0 || selectedSaleType) {
      setFiltersEnabled(true);
    }
    const copy = items.map((item, idx) => {
      item.originalIndex = idx;
      return item;
    });
    //filter by each function in the filter array
    const filtered = filtersArr.reduce((d, f) => {
      return d.filter((item) => {
        return f(item, user?.uid);
      });
    }, copy);
    //filter by sale type
    const filteredBySaleType = filtered.filter((item) => {
      if (selectedSaleType === 'buy') {
        return !!item?.lowestAsk;
      }
      if (selectedSaleType === 'rent') {
        return !!item?.priceHourly;
      }
      // allow bids on any item
      return true;
    });
    setFilteredItems(filteredBySaleType);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, items, selectedSaleType]);

  const searchAndSort = useCallback(
    (items: ExtendedItemCardPriced[]) => {
      return items.sort(sortFunction).filter(searchFunction);
    },
    [sortFunction, searchFunction],
  );

  const sortedAndSearchedFilteredItems =
    useMemo((): ExtendedItemCardPriced[] => {
      return searchAndSort(filteredItems);
    }, [searchAndSort, filteredItems]);

  const sortedAndSearchedItems = useMemo((): ExtendedItemCardPriced[] => {
    return searchAndSort(items);
  }, [searchAndSort, items]);

  const lastRowRef = useCallback(
    (node: HTMLElement) => {
      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setPageNumber((prev) => {
            return prev + 1;
          });
        }
      });
      if (node) {
        observer.current.observe(node);
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [items],
  );

  return (
    <>
      {parent === 'inventory' ? (
        <Droppable droppableId="catalogueGrid" direction="horizontal">
          {(provided) => {
            return (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  minHeight: '400px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}
                >
                  {!loading ? (
                    filtersEnabled ? (
                      filteredItems.length > 0 ? (
                        sortedAndSearchedFilteredItems.map(
                          (item, index, arr) => {
                            return (
                              <DraggableCatalogueGridItem
                                key={getItemKey(item.contract)}
                                item={item}
                                isDragEnabled={isDragEnabled}
                                handleSelectItem={handleSelectItem}
                                lastRowRef={
                                  index === arr.length - 20
                                    ? lastRowRef
                                    : undefined
                                }
                                expandedItem={expandedItem}
                              />
                            );
                          },
                        )
                      ) : (
                        <NoResults />
                      )
                    ) : items.length > 0 ? (
                      items
                        .map((item, idx) => {
                          item.originalIndex = idx;
                          return item;
                        })
                        .sort(sortFunction)
                        .filter(searchFunction)
                        .map((item, index, arr) => {
                          return (
                            <DraggableCatalogueGridItem
                              key={getItemKey(item.contract)}
                              item={item}
                              isDragEnabled={isDragEnabled}
                              handleSelectItem={handleSelectItem}
                              lastRowRef={
                                index === arr.length - 20
                                  ? lastRowRef
                                  : undefined
                              }
                              expandedItem={expandedItem}
                            />
                          );
                        })
                    ) : (
                      <Box>
                        <Typography variant="h6">
                          {`You don't own any items from `}
                          <Link href={`/game/${toKebabCase(title as string)}`}>
                            {title}
                          </Link>
                          {`. Why not get some?`}
                        </Typography>
                      </Box>
                    )
                  ) : (
                    <LottieLoader sx={{ height: '40px', width: '40px' }} />
                  )}
                </Box>
                {provided.placeholder}
              </Box>
            );
          }}
        </Droppable>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            width: '100%',
            // pl: 12,
            // pr: 8,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: isMobile ? 'center' : 'flex-start',
            }}
          >
            {!loading ? (
              filtersEnabled ? (
                filteredItems.length > 0 ? (
                  sortedAndSearchedFilteredItems.length > 0 ? (
                    sortedAndSearchedFilteredItems
                      .slice(0, (pageNumber + 1) * 60)
                      .map((item, index, arr) => {
                        return (
                          <CatalogueGridItem
                            key={getItemKey(item.contract)}
                            item={item}
                            handleSelectItem={handleSelectItem}
                            lastRowRef={
                              index === arr.length - 20 ? lastRowRef : undefined
                            }
                            expandedItem={expandedItem}
                          />
                        );
                      })
                  ) : (
                    <NoResults />
                  )
                ) : (
                  <NoResults />
                )
              ) : sortedAndSearchedItems.length > 0 ? (
                sortedAndSearchedItems
                  .slice(0, (pageNumber + 1) * 60)
                  .map((item, index, arr) => {
                    return (
                      <CatalogueGridItem
                        key={getItemKey(item.contract)}
                        item={item}
                        handleSelectItem={handleSelectItem}
                        lastRowRef={
                          index === arr.length - 20 ? lastRowRef : undefined
                        }
                        expandedItem={expandedItem}
                      />
                    );
                  })
              ) : (
                <NoResults />
              )
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <LottieLoader sx={{ height: '40px', width: '40px' }} />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}
